import Link from "next/link";
import type { TradeCount } from "@/lib/construction/types";
import { formatCount } from "@/lib/construction/normalise";
import { getTrade } from "@/lib/construction/trades";

type TradeListProps = {
  trades: TradeCount[];
  /** Place name for the accessible label, e.g. "Manchester". */
  place: string;
};

/** Trades found in a place, with counts. Each links to the matching trade landing page on this site. */
export function TradeList({ trades, place }: TradeListProps) {
  const items = trades
    .map((t) => ({ ...t, trade: getTrade(t.slug) }))
    .filter((t): t is TradeCount & { trade: NonNullable<ReturnType<typeof getTrade>> } => Boolean(t.trade));
  if (!items.length) return null;

  return (
    <ul aria-label={`Construction trades in ${place}`} className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {items.map(({ slug, count, trade }) => (
        <li key={slug} className="flex flex-col justify-between gap-3 rounded-2xl border border-ink/[0.08] bg-white p-5">
          <div className="flex items-baseline justify-between gap-3">
            <span className="font-display text-base font-bold tracking-tight text-ink">{trade.label}</span>
            <span className="font-display text-lg font-bold text-ink">{formatCount(count)}</span>
          </div>
          <Link
            href={`/industries/${trade.industry}`}
            className="text-sm font-medium text-ink-muted underline decoration-accent decoration-2 underline-offset-4 hover:text-ink"
          >
            Website design for {trade.plural}
          </Link>
        </li>
      ))}
    </ul>
  );
}
